/*
 * Team's Leads "Today by level" (PROD-9977): one row per level, from team_shift_summary.
 * Clicking a level narrows the rep picker to that level; clicking it again clears the filter.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Btn, LABEL_CLASS } from '../ui';
import { fetchTeamShiftSummary, leadsErrorReason } from './api';
import { Panel } from './MyLeadsView';
import { levelRowsView } from './my_leads';
import type { LevelRowView } from './my_leads';
import type { RepLevel } from '../../sales_team/api';

const REFRESH_MS = 5 * 60 * 1000;
const TH = `px-3.5 py-2 text-right first:text-left ${LABEL_CLASS}`;
const TD = 'px-3.5 py-2.5 text-right align-top tabular-nums first:text-left';
const DELTA_TONE = { success: 'text-state-success-text', error: 'text-state-error-text', '': '' };

interface Props {
  level: RepLevel | '';
  onLevel: (level: RepLevel | '') => void;
}

function TeamShiftSummary({ level, onLevel }: Props) {
  const [rows, setRows] = useState<LevelRowView[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchTeamShiftSummary()
      .then((d) => {
        setRows(levelRowsView(d.levels));
        setError('');
      })
      .catch((e: unknown) => {
        console.error('[team_shift_summary] load failed', e);
        setError(leadsErrorReason(e) || 'Could not load today by level');
      });
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  return (
    <div className="mx-[5%] mt-5 max-sm:mx-3">
      <Panel
        testId="team-shift-summary"
        title="Today by level (Super User View Only)"
        sub="Each level's tasks since its earliest shift start today. Click a level to list its reps."
        aside={level ? `Showing ${level} reps` : undefined}
      >
        {error ? (
          <p
            role="alert"
            className="m-0 flex flex-wrap items-center gap-2 px-5 py-4 text-xs text-state-error-text"
          >
            {error}
            <Btn size="row" onClick={load}>
              Try again
            </Btn>
          </p>
        ) : !rows ? (
          <p className="m-0 px-5 py-4 text-xs text-content-muted">Loading today by level…</p>
        ) : (
          <>
            <div className="overflow-x-auto max-md:hidden">
              <table
                data-testid="team-shift-table"
                className="w-full min-w-[760px] text-[12.5px]"
                style={{ borderCollapse: 'collapse', margin: 0, border: 0 }}
              >
                <thead>
                  <tr className="border-x-0 border-t-0 border-b border-line-subtle bg-surface-sunken">
                    <th className={`${TH} md:pl-5`}>Level</th>
                    <th className={TH}>Open at start</th>
                    <th className={TH}>New</th>
                    <th className={TH}>Done</th>
                    <th className={TH}>Overdue now</th>
                    <th className={TH}>Since shift start</th>
                    <th className={TH}>Not due yet</th>
                    <th className={`${TH} md:pr-5`}>Older backlog</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const picked = level === r.level;
                    return (
                      <tr
                        key={r.level}
                        data-testid={`team-shift-row-${r.level}`}
                        aria-selected={picked}
                        tabIndex={0}
                        onClick={() => onLevel(picked ? '' : r.level)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onLevel(picked ? '' : r.level);
                          }
                        }}
                        className={`cursor-pointer border-x-0 border-b-0 border-t border-line-subtle text-content-primary hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary ${
                          picked ? 'bg-state-info-bg' : ''
                        }`}
                      >
                        <td className={`${TD} md:pl-5`}>
                          <span className="font-semibold">{r.level}</span>
                          {r.noShift ? null : (
                            <span className="block text-[11px] text-content-muted">
                              {r.caption}
                            </span>
                          )}
                        </td>
                        {r.noShift ? (
                          <td colSpan={7} className={`${TD} !text-left text-content-muted md:pr-5`}>
                            No shifts today
                          </td>
                        ) : (
                          <>
                            <td className={TD}>{r.openAtStart}</td>
                            <td className={TD}>{r.cameIn}</td>
                            <td className={TD}>{r.done}</td>
                            <td className={`${TD} font-bold text-state-error-text`}>{r.overdue}</td>
                            <td className={`${TD} font-semibold ${DELTA_TONE[r.change.tone]}`}>
                              {r.change.text || 'no change'}
                            </td>
                            <td className={TD}>{r.notDue}</td>
                            <td className={`${TD} md:pr-5`}>{r.backlog}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ul data-testid="team-shift-cards" className="m-0 list-none p-0 md:hidden">
              {rows.map((r) => {
                const picked = level === r.level;
                const stat = (label: string, value: React.ReactNode, tone = '') => (
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[11px] text-content-muted">{label}</span>
                    <span className={`tabular-nums ${tone}`}>{value}</span>
                  </div>
                );
                return (
                  <li
                    key={r.level}
                    className="border-x-0 border-b-0 border-t border-line-subtle first:border-t-0"
                  >
                    <button
                      type="button"
                      data-testid={`team-shift-card-${r.level}`}
                      aria-pressed={picked}
                      onClick={() => onLevel(picked ? '' : r.level)}
                      className={`flex w-full flex-col gap-2 border-0 px-3 py-3 text-left text-[12.5px] text-content-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary ${
                        picked ? 'bg-state-info-bg' : 'bg-transparent'
                      }`}
                    >
                      <span>
                        <span className="font-semibold">{r.level}</span>
                        {r.noShift ? null : (
                          <span className="block text-[11px] text-content-muted">{r.caption}</span>
                        )}
                      </span>
                      {r.noShift ? (
                        <span className="text-content-muted">No shifts today</span>
                      ) : (
                        <span className="grid grid-cols-2 gap-x-4 gap-y-1">
                          {stat('Overdue now', r.overdue, 'font-bold text-state-error-text')}
                          {stat(
                            'Since shift start',
                            r.change.text || 'no change',
                            `font-semibold ${DELTA_TONE[r.change.tone]}`
                          )}
                          {stat('Open at start', r.openAtStart)}
                          {stat('New', r.cameIn)}
                          {stat('Done', r.done)}
                          {stat('Not due yet', r.notDue)}
                          {stat('Older backlog', r.backlog)}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Panel>
    </div>
  );
}

export default TeamShiftSummary;
